import type { BatchBookingRequest } from "@bw/shared/types/generated";

const STORAGE_PREFIX = "bw:booking-attempt:";

function storageKey(facilityId: string, userId: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(facilityId)}:${encodeURIComponent(userId)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function parseRequest(value: unknown): BatchBookingRequest | null {
  if (
    !isRecord(value) ||
    !nonEmptyString(value.idempotency_key) ||
    !nonEmptyString(value.stripe_payment_method_id) ||
    (value.campaign_id != null && !nonEmptyString(value.campaign_id)) ||
    !Array.isArray(value.slots) ||
    value.slots.length === 0
  ) {
    return null;
  }

  const slots: BatchBookingRequest["slots"] = [];
  for (const candidate of value.slots) {
    if (
      !isRecord(candidate) ||
      !nonEmptyString(candidate.court_id) ||
      !nonEmptyString(candidate.start_time) ||
      !nonEmptyString(candidate.end_time) ||
      !nonEmptyString(candidate.timezone) ||
      !Number.isInteger(candidate.player_count) ||
      !Number.isInteger(candidate.non_member_player_count)
    ) {
      return null;
    }
    slots.push({
      court_id: candidate.court_id,
      start_time: candidate.start_time,
      end_time: candidate.end_time,
      timezone: candidate.timezone,
      player_count: candidate.player_count as number,
      non_member_player_count: candidate.non_member_player_count as number,
    });
  }

  return {
    ...(value.campaign_id != null
      ? { campaign_id: value.campaign_id as string }
      : {}),
    idempotency_key: value.idempotency_key,
    stripe_payment_method_id: value.stripe_payment_method_id,
    slots,
  };
}

function isSameRequest(
  left: BatchBookingRequest,
  right: BatchBookingRequest,
): boolean {
  return (
    left.idempotency_key === right.idempotency_key &&
    left.stripe_payment_method_id === right.stripe_payment_method_id &&
    (left.campaign_id ?? null) === (right.campaign_id ?? null) &&
    left.slots.length === right.slots.length &&
    left.slots.every((slot, index) => {
      const other = right.slots[index];
      return (
        other !== undefined &&
        slot.court_id === other.court_id &&
        slot.start_time === other.start_time &&
        slot.end_time === other.end_time &&
        slot.timezone === other.timezone &&
        slot.player_count === other.player_count &&
        slot.non_member_player_count === other.non_member_player_count
      );
    })
  );
}

export function loadBookingAttempt(
  facilityId: string,
  userId: string,
): BatchBookingRequest | null {
  try {
    const key = storageKey(facilityId, userId);
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return null;
    const envelope: unknown = JSON.parse(raw);
    if (
      !isRecord(envelope) ||
      envelope.facilityId !== facilityId ||
      envelope.userId !== userId
    ) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    const request = parseRequest(envelope.request);
    if (request === null) window.sessionStorage.removeItem(key);
    return request;
  } catch {
    return null;
  }
}

export function storeBookingAttempt(
  facilityId: string,
  userId: string,
  request: BatchBookingRequest,
): void {
  try {
    window.sessionStorage.setItem(
      storageKey(facilityId, userId),
      JSON.stringify({ facilityId, userId, request }),
    );
  } catch {
    // In-memory state still preserves the request for this mounted page.
  }
}

export function clearBookingAttempt(
  facilityId: string,
  userId: string,
  expected: BatchBookingRequest,
): void {
  const stored = loadBookingAttempt(facilityId, userId);
  if (stored === null) return;
  if (!isSameRequest(stored, expected)) return;
  try {
    window.sessionStorage.removeItem(storageKey(facilityId, userId));
  } catch {
    // Storage may become unavailable after the attempt was written.
  }
}
